import React, { FC, useEffect, useState } from "react";
import queryString from "query-string";
import "./index.scss";
import { getOop } from "../../../../services/oop";
import { Match, OopData } from "../types";
import { reformatScores } from "../../../../dynamic/DrawSheets/src/utils";
import constants from "./../../../../constants";

interface Props {
  tournamentName?: string;
  categoryCode?: string;
  orderOfPlayDate?: string;
  tournamentKey?: string;
  prizeMoney?: string;
  location?: string;
  weekOf?: string;
  dateReleased?: string;
  headerNote?: string;
  footerNote?: string;
  circuitCode?: string;
}

const PrintOrderOfPlay: FC<Props> = ({
  tournamentName,
  categoryCode,
  tournamentKey,
  orderOfPlayDate,
  // prizeMoney,
  location,
  weekOf,
  dateReleased,
  headerNote,
  footerNote,
  circuitCode
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [results, setResults] = useState<(Match | null)[][]>([]);
  const [captions, setCaptions] = useState<string[]>([]);

  useEffect(() => {
    document.getElementsByTagName("html")[0].classList.add("print-version");
    (async () => {
      const params = queryString.parse(window.location.search);
      if (params.orderOfPlayDayId) {
        setIsLoading(true);
        const data: OopData[] = await getOop(+params.orderOfPlayDayId);
        const courtName = data.map(({ courtName }) => courtName);
        const matches = data.map(({ matches }) => matches);
        const tr = new Array(
          Math.max.apply(Math, matches.map(item => item.length))
        )
          .fill(0)
          .map((_, i) => i);
        const td = new Array(matches.length).fill(0).map((_, i) => i);
        const table = tr.map(trKey =>
          td.map(tdKey =>
            matches[tdKey][trKey] ? matches[tdKey][trKey] : null
          )
        );
        setCaptions(courtName);
        setResults(table);
        setIsLoading(false);
      }
    })();
    return () => {
      document
        .getElementsByTagName("html")[0]
        .classList.remove("print-version");
    };
  }, []);

  const WTT = ["MT", "WT", "JT"];

  return (
    <div className="print-order">
      <table className="print-order__head">
        <tbody>
          <tr>
            <th align="left" colSpan={2} className="print-order__head--title">
              <span className="bold">{tournamentName}</span>
              <br />
              <i>{categoryCode}</i>
            </th>
            <th align="left">
              ORDER OF PLAY <br />
              {orderOfPlayDate}
            </th>
            <th>
              {circuitCode && WTT.includes(circuitCode) ? (
                <div className="print-order__logo"></div>
              ) : null}
            </th>
          </tr>
          <tr>
            <td>
              <b>Week Of</b>
              <br />
              {weekOf}
            </td>
            <td>
              <b>City, Country</b>
              <br />
              {location}
            </td>
            <td>
              <b>Tournament Key</b>
              <br />
              {tournamentKey}
            </td>
            <td>
              <a onClick={() => window.print()} className="print-order__btn">
                Print
              </a>
            </td>
          </tr>
        </tbody>
      </table>
      {isLoading ? null : captions.length ? (
        <>
          <div className="print-order__revised">{headerNote}</div>
          <table className="print-order__body">
            <thead>
              <tr>
                <th>No.</th>
                {captions.map((text, key) => (
                  <th key={key}>{text}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map((match, trKey) => (
                <tr key={trKey}>
                  <td key={`${trKey}-no`}>{trKey + 1}.</td>
                  {match.map((item, tdKey) => (
                    <td key={`${trKey}-${tdKey}`}>
                      {!item ? null : (
                        <>
                          <strong>{item.schedule}</strong>
                          <br />
                          {item.matchDescription}
                          <br />
                          <br />
                          {item.teams.map(
                            (
                              { players, seeding, entryStatus, isWinner },
                              teamKey
                            ) => (
                              <span key={teamKey}>
                                {teamKey !== 0 ? (
                                  <>
                                    <br />
                                    vs.
                                    <br />
                                  </>
                                ) : null}
                                <span className={isWinner ? "bold" : ""}>
                                  {players.map((player, playerKey) =>
                                    player ? (
                                      <span key={player.playerId}>
                                        {playerKey !== 0 ? "/" : null}
                                        {player.givenName} {player.familyName}
                                        {!constants.omitNationality.includes(
                                          player.nationality
                                        )
                                          ? `(${player.nationality})`
                                          : null}
                                        {seeding
                                          ? `[${seeding}]`
                                          : entryStatus
                                          ? `(${entryStatus})`
                                          : null}
                                      </span>
                                    ) : null
                                  )}
                                </span>
                              </span>
                            )
                          )}
                          <br />
                          <br />
                          {reformatScores(item.teams)
                            .filter(({ scoreA, scoreB }) => scoreA && scoreB)
                            .map(({ scoreA, scoreB }, scoreKey: number) => (
                              <span key={scoreKey}>
                                {scoreA !== null
                                  ? `${scoreA.score}${
                                      scoreA.losingScore !== null
                                        ? `(${scoreA.losingScore})`
                                        : ""
                                    }`
                                  : null}
                                -
                                {scoreB !== null
                                  ? `${scoreB.score}${
                                      scoreB.losingScore !== null
                                        ? `(${scoreB.losingScore})`
                                        : ""
                                    }`
                                  : null}
                                &nbsp;
                              </span>
                            ))}
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <table className="print-order__footer">
            <tbody>
              <tr>
                <td>
                  {footerNote ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: footerNote
                      }}
                    />
                  ) : null}
                </td>
                <td>
                  <strong>Order of Play Released</strong>
                  <br />
                  {dateReleased}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="print-order__notes">
            <strong>All match times are shown in local time.</strong>
            <strong>
              At combined Men's and Women's tournaments, empty cells may
              indicate scheduled matches for a related event.
            </strong>
          </div>
        </>
      ) : (
        <div className="print-order__empty">
          Cannot show Printable OOP. <br /> There is no OOP data on file for{" "}
          {orderOfPlayDate}
        </div>
      )}
    </div>
  );
};

export default PrintOrderOfPlay;
